import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useT } from "~/i18n";
import {
  type Toast,
  type ToastInput,
  type ToastTone,
  dismissToast,
  enqueueToast,
  toastDuration,
  updateToast,
  visibleToasts,
} from "~/lib/ui/toast-queue";
import { Icon, type IconName } from "./icon";

export type { ToastInput, ToastTone } from "~/lib/ui/toast-queue";

export type ToastApi = {
  /** Show a result. Returns its id so pending work can be updated in place. */
  show: (toast: ToastInput) => string;
  update: (id: string, patch: Partial<ToastInput>) => void;
  dismiss: (id: string) => void;
};

const NOOP: ToastApi = { show: () => "", update: () => undefined, dismiss: () => undefined };

const ToastContext = createContext<ToastApi | null>(null);

const GLYPH: Record<ToastTone, IconName> = {
  success: "success",
  info: "info",
  pending: "refresh",
  partial: "warning",
  error: "error",
};

let sequence = 0;

/**
 * One small queue of result toasts for the whole app. A toast names what
 * happened ("CSV exportado · 128 itens"), carries at most one follow-up
 * action and a close button. Success fades after a few seconds (paused while
 * hovered or focused); errors and pending work stay until closed. Three at
 * most on screen, bottom-right, clear of the header actions and drawers'
 * decision buttons.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Toast[]>([]);

  const show = useCallback((input: ToastInput) => {
    sequence += 1;
    const id = `toast-${sequence}`;
    setQueue((current) => enqueueToast(current, { ...input, id, createdAt: Date.now() }));
    return id;
  }, []);
  const update = useCallback((id: string, patch: Partial<ToastInput>) => {
    setQueue((current) => updateToast(current, id, patch));
  }, []);
  const dismiss = useCallback((id: string) => {
    setQueue((current) => dismissToast(current, id));
  }, []);

  const api = useMemo(() => ({ show, update, dismiss }), [show, update, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastRegion toasts={visibleToasts(queue)} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  // Outside the provider (unit tests, isolated stories) toasts are a no-op.
  return useContext(ToastContext) ?? NOOP;
}

function ToastRegion({
  toasts,
  onDismiss,
}: {
  toasts: readonly Toast[];
  onDismiss: (id: string) => void;
}) {
  const t = useT();
  return (
    <section className="toast-region" aria-label={t("ui.toast.region")}>
      <ol className="toast-list">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={() => onDismiss(toast.id)} />
        ))}
      </ol>
    </section>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const t = useT();
  const [paused, setPaused] = useState(false);
  const duration = toastDuration(toast.tone);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Restart the timer whenever the toast changes tone (pending → success).
  useEffect(() => {
    if (duration === null || paused) return;
    const timer = window.setTimeout(() => onDismissRef.current(), duration);
    return () => window.clearTimeout(timer);
  }, [duration, paused]);

  const urgent = toast.tone === "error";
  return (
    <li
      className="toast"
      data-tone={toast.tone}
      role={urgent ? "alert" : "status"}
      aria-live={urgent ? "assertive" : "polite"}
      aria-atomic="true"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="toast__glyph">
        <Icon name={GLYPH[toast.tone]} size={18} />
      </span>
      <span className="toast__copy">
        <strong className="toast__title">{toast.title}</strong>
        {toast.body ? <span className="toast__body">{toast.body}</span> : null}
      </span>
      {toast.action ? (
        <button
          type="button"
          className="btn btn--sm toast__action"
          onClick={() => {
            toast.action?.onClick();
            onDismiss();
          }}
        >
          {toast.action.label}
        </button>
      ) : null}
      <button
        type="button"
        className="toast__close"
        onClick={onDismiss}
        aria-label={t("ui.toast.close")}
        title={t("ui.toast.close")}
      >
        <Icon name="close" size={16} />
      </button>
    </li>
  );
}
